import React, { useState, useEffect } from "react";
import {
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  Zap,
  BarChart3,
  ArrowRight,
  Menu,
  X,
  Smartphone,
  Download,
  Globe,
  Phone,
  MessageSquare,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import webImage1 from "./../Images/webImage1.png";
import Image1 from "./../Images/Image1.png";
import Image2 from "./../Images/Image2.png";
import Image3 from "./../Images/Image3.png";
import Image4 from "./../Images/Image4.png";

function useBreakpoint() {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return { isMobile: w < 640, isTablet: w < 900 };
}

const MainPage = () => {
  const { t, i18n } = useTranslation();
  const { isMobile, isTablet } = useBreakpoint();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  useEffect(() => {
    try {
      const hasVisited = localStorage.getItem("hasVisited");
      const savedLang = localStorage.getItem("lang");
      if (!hasVisited) {
        setShowLanguageModal(true);
      } else if (savedLang) {
        i18n.changeLanguage(JSON.parse(savedLang));
      }
    } catch {
      localStorage.removeItem("lang");
      localStorage.removeItem("hasVisited");
      setShowLanguageModal(true);
    }
  }, [i18n]);

  const handleLanguageSelect = (lang) => {
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem("lang", JSON.stringify(lang));
      localStorage.setItem("hasVisited", "true");
      setShowLanguageModal(false);
    } catch {}
  };

  const changeLanguage = (lang) => {
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem("lang", JSON.stringify(lang));
      setShowLanguageDropdown(false);
    } catch {}
  };

  const scrollToApp = () =>
    document
      .getElementById("app")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const id = setInterval(
      () => setActiveFeature((prev) => (prev + 1) % 3),
      3000,
    );
    return () => clearInterval(id);
  }, []);

  const features = [
    {
      icon: <Calendar size={22} />,
      title: t("SmartScheduling"),
      description: t("SmartSchedulingDesc"),
      stat: t("SmartSchedulingStat"),
    },
    {
      icon: <Users size={22} />,
      title: t("ClientManagement"),
      description: t("ClientManagementDesc"),
      stat: t("ClientManagementStat"),
    },
    {
      icon: <BarChart3 size={22} />,
      title: t("AnalyticsInsights"),
      description: t("AnalyticsInsightsDesc"),
      stat: t("AnalyticsInsightsStat"),
    },
  ];

  const stats = [
    { number: "10K+", label: t("ActiveUsers") },
    { number: "1M+", label: t("AppointmentsBooked") },
    { number: "99.9%", label: t("Uptime") },
    { number: "4.9/5", label: t("UserRating") },
  ];

  const benefits = [
    {
      icon: <Clock size={18} />,
      title: t("SaveHoursDaily"),
      description: t("SaveHoursDesc"),
    },
    {
      icon: <CheckCircle size={18} />,
      title: t("NeverMissAppointment"),
      description: t("NeverMissDesc"),
    },
    {
      icon: <Zap size={18} />,
      title: t("InstantSetup"),
      description: t("InstantSetupDesc"),
    },
    {
      icon: <TrendingUp size={18} />,
      title: t("BoostRevenue"),
      description: t("BoostRevenueDesc"),
    },
  ];

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
    { code: "he", name: "עברית", flag: "🇮🇱" },
    { code: "sv", name: "Svenska", flag: "🇸🇪" },
  ];

  const curLang =
    languages.find((l) => l.code === i18n.language) || languages[0];
  const GOOGLE_PLAY =
    "https://play.google.com/store/apps/details?id=com.mohammad1998a.NeholTorem";
  const APP_STORE = "https://apps.apple.com/us/app/neholtorem/id6755939980";

  // Opens store links directly — no popup, no blocked redirects
  const openStore = (url) => (e) => {
    e.preventDefault();
    window.location.href = url;
  };

  const px = isMobile ? 16 : 24;
  const sectionPy = isMobile ? 56 : 80;

  return (
    <div style={s.root}>
      <style>{css}</style>

      {/* ─── Language Modal ─── */}
      {showLanguageModal && (
        <div style={s.overlay}>
          <div
            style={{ ...s.modal, padding: isMobile ? 20 : 32 }}
            className="nt-up"
          >
            <div style={s.modalHead}>
              <div style={s.globeRing}>
                <Globe size={26} color="#2563eb" />
              </div>
              <h2 style={s.modalTitle}>Welcome to NeholTorem</h2>
              <p style={s.modalSub}>Select your preferred language</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {languages.map((l) => (
                <button
                  key={l.code}
                  className="nt-lang-btn"
                  style={s.langBtn}
                  onClick={() => handleLanguageSelect(l.code)}
                >
                  <span style={{ fontSize: 22 }}>{l.flag}</span>
                  <span style={s.langBtnName}>{l.name}</span>
                  <ArrowRight
                    size={13}
                    style={{ color: "#94a3b8", marginLeft: "auto" }}
                  />
                </button>
              ))}
            </div>
            <p style={s.modalHint}>You can change this later from the menu</p>
          </div>
        </div>
      )}

      {/* ─── TOP BAR: Download + Contact ─── */}
      <div style={s.topBar}>
        <div style={s.topBarInner}>
          {/* Contact buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={s.topBarLabel}>{t("Contact") || "Contact"}:</span>
            {[
              {
                href: "https://wa.me/972527477128",
                label: t("WhatsApp") || "WhatsApp",
                color: "#16a34a",
                bg: "#dcfce7",
                icon: (
                  <svg
                    style={{ width: 13, height: 13 }}
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                ),
              },
              {
                href: "tel:+972527477128",
                label: t("Phone") || "Phone",
                color: "#1d4ed8",
                bg: "#dbeafe",
                icon: <Phone size={13} />,
              },
              {
                href: "sms:+972527477128",
                label: t("SMS") || "SMS",
                color: "#7c3aed",
                bg: "#ede9fe",
                icon: <MessageSquare size={13} />,
              },
            ].map((c) => (
              <a
                key={c.href}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="nt-topbtn"
                style={{ ...s.topContactBtn, color: c.color, background: c.bg }}
              >
                {c.icon}
                {!isMobile && <span>{c.label}</span>}
              </a>
            ))}
          </div>

          {/* Download buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {!isMobile && (
              <span style={s.topBarLabel}>
                {t("DownloadNow") || "Download"}:
              </span>
            )}
            <a
              href={GOOGLE_PLAY}
              onClick={openStore(GOOGLE_PLAY)}
              className="nt-topbtn"
              style={{ ...s.topStoreBtn, background: "#1a1a1a", color: "#fff" }}
            >
              <svg
                style={{ width: 14, height: 14 }}
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              <span>
                {isMobile ? "Android" : t("GooglePlay") || "Google Play"}
              </span>
            </a>
            <a
              href={APP_STORE}
              onClick={openStore(APP_STORE)}
              className="nt-topbtn"
              style={{ ...s.topStoreBtn, background: "#1a1a1a", color: "#fff" }}
            >
              <svg
                style={{ width: 14, height: 14 }}
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
              </svg>
              <span>{isMobile ? "iOS" : t("AppStore") || "App Store"}</span>
            </a>
          </div>
        </div>
      </div>

      {/* ─── Nav ─── */}
      <nav style={{ ...s.nav, ...(scrolled ? s.navScrolled : {}) }}>
        <div style={{ ...s.navRow, padding: `14px ${px}px` }}>
          <div style={s.navLogo}>
            <div style={s.logoMark}>✂</div>
            <span style={{ ...s.brandName, fontSize: isMobile ? 17 : 21 }}>
              NeholTorem
            </span>
          </div>

          {/* Desktop links */}
          <div style={s.desktopLinks} className="nt-desk">
            {["#features", "#app", "#benefits", "#contact"].map((h, i) => (
              <a key={h} href={h} style={s.navLink} className="nt-navlink">
                {
                  [
                    t("Features"),
                    t("MobileApp"),
                    t("Benefits"),
                    t("Contact") || "Contact",
                  ][i]
                }
              </a>
            ))}
            <div style={{ position: "relative" }}>
              <button
                style={s.langToggle}
                className="nt-ghost"
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <Globe size={13} color="#64748b" />
                <span style={{ fontSize: 17 }}>{curLang.flag}</span>
                <span style={s.langToggleName}>{curLang.name}</span>
              </button>
              {showLanguageDropdown && (
                <div style={s.langDrop}>
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      className="nt-dropitem"
                      style={{
                        ...s.langDropItem,
                        ...(i18n.language === l.code ? s.langDropActive : {}),
                      }}
                      onClick={() => changeLanguage(l.code)}
                    >
                      <span style={{ fontSize: 17 }}>{l.flag}</span>
                      <span style={{ flex: 1, textAlign: "left" }}>
                        {l.name}
                      </span>
                      {i18n.language === l.code && (
                        <CheckCircle size={12} style={{ color: "#2563eb" }} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                {
                  href: "https://www.instagram.com/neholtorem/",
                  label: "Instagram",
                  icon: (
                    <svg
                      style={{ width: 15, height: 15 }}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  ),
                },
                {
                  href: "https://www.facebook.com/profile.php?id=61583627140017",
                  label: "Facebook",
                  icon: (
                    <svg
                      style={{ width: 15, height: 15 }}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  ),
                },
              ].map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nt-social"
                  style={s.socialIcon}
                  aria-label={item.label}
                >
                  {item.icon}
                </a>
              ))}
            </div>
            <button
              className="nt-primary"
              style={s.navCta}
              onClick={scrollToApp}
            >
              {t("GetStarted")}
            </button>
          </div>

          <button
            style={s.burger}
            className="nt-mob"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X size={22} color="#1e293b" />
            ) : (
              <Menu size={22} color="#1e293b" />
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <div style={s.mobileMenu} className="nt-up">
            {["#features", "#app", "#benefits", "#contact"].map((h, i) => (
              <a
                key={h}
                href={h}
                style={s.mobileLink}
                onClick={() => setMobileMenuOpen(false)}
              >
                {
                  [
                    t("Features"),
                    t("MobileApp"),
                    t("Benefits"),
                    t("Contact") || "Contact",
                  ][i]
                }
              </a>
            ))}
            <div
              style={{ height: 1, background: "#e2e8f0", margin: "4px 0" }}
            />
            {languages.map((l) => (
              <button
                key={l.code}
                className="nt-lang-btn"
                style={{
                  ...s.langBtn,
                  ...(i18n.language === l.code
                    ? {
                        borderColor: "#2563eb",
                        color: "#2563eb",
                        background: "#eff6ff",
                      }
                    : {}),
                }}
                onClick={() => {
                  changeLanguage(l.code);
                  setMobileMenuOpen(false);
                }}
              >
                <span style={{ fontSize: 20 }}>{l.flag}</span>
                <span style={s.langBtnName}>{l.name}</span>
                {i18n.language === l.code && (
                  <CheckCircle
                    size={13}
                    style={{ marginLeft: "auto", color: "#2563eb" }}
                  />
                )}
              </button>
            ))}
            <button
              className="nt-primary"
              style={{
                ...s.navCta,
                width: "100%",
                marginTop: 8,
                justifyContent: "center",
              }}
              onClick={() => {
                scrollToApp();
                setMobileMenuOpen(false);
              }}
            >
              {t("GetStarted")}
            </button>
          </div>
        )}
      </nav>

      {/* ═══════════════ HERO ═══════════════ */}
      <section
        style={{
          ...s.section,
          paddingTop: isMobile ? 130 : 160,
          paddingBottom: isMobile ? 48 : 80,
          paddingLeft: px,
          paddingRight: px,
          background:
            "linear-gradient(160deg, #f0f7ff 0%, #ffffff 50%, #f8faff 100%)",
        }}
      >
        <div
          style={{
            maxWidth: isMobile ? "100%" : 760,
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div style={s.badge} className="nt-up">
            <span style={{ color: "#2563eb", fontSize: 10 }}>✦</span>
            <span style={s.badgeText}>{t("TrustedByProfessionals")}</span>
          </div>
          <h1
            style={{
              ...s.h1,
              fontSize: isMobile
                ? "clamp(32px,9vw,46px)"
                : "clamp(42px,7vw,72px)",
              marginBottom: isMobile ? 14 : 20,
            }}
            className="nt-up"
          >
            {t("AppointmentManagement")}{" "}
            <em style={{ fontStyle: "italic", color: "#2563eb" }}>
              {t("Simplified")}
            </em>
          </h1>
          <p
            style={{
              ...s.heroSub,
              fontSize: isMobile ? 13 : 15,
              marginBottom: isMobile ? 28 : 36,
            }}
            className="nt-up"
          >
            {t("HeroDescription")}
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
              flexDirection: isMobile ? "column" : "row",
              marginBottom: isMobile ? 24 : 32,
            }}
          >
            <button
              className="nt-primary"
              style={{ ...s.btnPrimary, justifyContent: "center" }}
              onClick={scrollToApp}
            >
              {t("StartFreeTrial")} <ArrowRight size={15} />
            </button>
            <button
              className="nt-ghost"
              style={{ ...s.btnGhost, justifyContent: "center" }}
            >
              {t("WatchDemo")}
            </button>
          </div>

          {/* ── Hero: Download + Contact ── */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 14,
              marginBottom: isMobile ? 32 : 44,
            }}
          >
            {/* Download row */}
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <a
                href={GOOGLE_PLAY}
                onClick={openStore(GOOGLE_PLAY)}
                className="nt-herobtn"
                style={s.heroStoreBtn}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: "#1a1a1a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    style={{ width: 20, height: 20 }}
                    viewBox="0 0 24 24"
                    fill="white"
                  >
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                </div>
                <div style={{ textAlign: "left" }}>
                  <p
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: 9,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      marginBottom: 1,
                    }}
                  >
                    {t("GetItOnGooglePlay") || "Get it on"}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Playfair Display',serif",
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    {t("GooglePlay") || "Google Play"}
                  </p>
                </div>
              </a>
              <a
                href={APP_STORE}
                onClick={openStore(APP_STORE)}
                className="nt-herobtn"
                style={s.heroStoreBtn}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: "#1a1a1a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    style={{ width: 20, height: 20 }}
                    viewBox="0 0 24 24"
                    fill="white"
                  >
                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                  </svg>
                </div>
                <div style={{ textAlign: "left" }}>
                  <p
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: 9,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      marginBottom: 1,
                    }}
                  >
                    {t("DownloadOnTheAppStore") || "Download on the"}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Playfair Display',serif",
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    {t("AppStore") || "App Store"}
                  </p>
                </div>
              </a>
            </div>

            {/* Divider */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                maxWidth: 460,
              }}
            >
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              <span
                style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 11,
                  color: "#94a3b8",
                  whiteSpace: "nowrap",
                }}
              >
                {t("Contact") || "Contact us"}
              </span>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            </div>

            {/* Contact row */}
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {[
                {
                  href: "https://wa.me/972527477128",
                  label: t("WhatsApp") || "WhatsApp",
                  color: "#16a34a",
                  bg: "#f0fdf4",
                  border: "#bbf7d0",
                  icon: (
                    <svg
                      style={{ width: 16, height: 16 }}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                  ),
                },
                {
                  href: "tel:+972527477128",
                  label: t("Phone") || "Phone",
                  color: "#1d4ed8",
                  bg: "#eff6ff",
                  border: "#bfdbfe",
                  icon: <Phone size={16} />,
                },
                {
                  href: "sms:+972527477128",
                  label: t("SMS") || "SMS",
                  color: "#7c3aed",
                  bg: "#faf5ff",
                  border: "#ddd6fe",
                  icon: <MessageSquare size={16} />,
                },
              ].map((c) => (
                <a
                  key={c.href}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nt-herocontact"
                  style={{
                    ...s.heroContactBtn,
                    color: c.color,
                    background: c.bg,
                    borderColor: c.border,
                  }}
                >
                  {c.icon}
                  <span>{c.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)",
              maxWidth: isMobile ? "100%" : 600,
              margin: "0 auto",
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 24px rgba(37,99,235,0.08)",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            {stats.map((stat, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  padding: isMobile ? "16px 8px" : "20px 8px",
                  borderRight: i < 3 ? "1px solid #e2e8f0" : "none",
                  borderBottom:
                    isMobile && i < 2 ? "1px solid #e2e8f0" : "none",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: isMobile ? 22 : 28,
                    fontWeight: 700,
                    color: "#2563eb",
                    marginBottom: 4,
                  }}
                >
                  {stat.number}
                </div>
                <div
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: 11,
                    color: "#94a3b8",
                    letterSpacing: "0.05em",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES ═══════════════ */}
      <section
        id="features"
        style={{
          ...s.section,
          padding: `${sectionPy}px ${px}px`,
          background: "#fff",
        }}
      >
        <div style={s.inner}>
          <div style={s.secHead}>
            <p style={s.eyebrow}>✦ {t("EverythingYouNeedTo")} ✦</p>
            <h2 style={s.h2}>{t("Succeed")}</h2>
            <p style={s.secSub}>{t("BuiltForProfessionals")}</p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fit,minmax(280px,1fr))",
              gap: 20,
            }}
          >
            {features.map((f, i) => (
              <div
                key={i}
                className="nt-fcard"
                style={{
                  ...s.fCard,
                  ...(activeFeature === i ? s.fCardOn : {}),
                }}
                onMouseEnter={() => setActiveFeature(i)}
              >
                <div
                  style={{
                    ...s.fIcon,
                    ...(activeFeature === i ? s.fIconOn : {}),
                  }}
                >
                  {f.icon}
                </div>
                <h3 style={s.fTitle}>{f.title}</h3>
                <p style={s.fDesc}>{f.description}</p>
                <div style={s.fStat}>
                  <span style={s.fStatText}>{f.stat}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ APP SECTION ═══════════════ */}
      <section
        id="app"
        style={{
          ...s.section,
          padding: `${sectionPy}px ${px}px`,
          background: "#f8faff",
        }}
      >
        <div style={s.inner}>
          <div style={s.secHead}>
            <p style={s.eyebrow}>✦ {t("AvailableOnMobile")} ✦</p>
            <h2 style={s.h2}>
              {t("ManageEverything")}{" "}
              <em style={{ fontStyle: "italic", color: "#2563eb" }}>
                {t("OnTheGo")}
              </em>
            </h2>
            <p style={s.secSub}>{t("MobileAppDescription")}</p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr",
              gap: isTablet ? 36 : 56,
              alignItems: "center",
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: "10%",
                  background:
                    "radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)",
                  pointerEvents: "none",
                  borderRadius: "50%",
                }}
              />
              <div
                style={{
                  width: isMobile ? 170 : 230,
                  height: isMobile ? 350 : 470,
                  borderRadius: 40,
                  border: "8px solid #e2e8f0",
                  background: "#fff",
                  overflow: "hidden",
                  boxShadow: "0 32px 80px rgba(37,99,235,0.15)",
                  position: "relative",
                  zIndex: 2,
                }}
                className="nt-phone"
              >
                <img
                  src={webImage1}
                  alt="App"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            </div>

            <div>
              <h3
                style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: isMobile ? 23 : 29,
                  fontWeight: 700,
                  color: "#1e293b",
                  marginBottom: 10,
                }}
              >
                {t("YourBusinessInYourPocket")}
              </h3>
              <p
                style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 14,
                  color: "#64748b",
                  lineHeight: 1.8,
                  marginBottom: 22,
                }}
              >
                {t("MobileAppFullDescription")}
              </p>
              {[
                {
                  icon: <Calendar size={17} />,
                  title: t("RealTimeSync"),
                  desc: t("RealTimeSyncDesc"),
                },
                {
                  icon: <Zap size={17} />,
                  title: t("PushNotifications"),
                  desc: t("PushNotificationsDesc"),
                },
                {
                  icon: <Smartphone size={17} />,
                  title: t("WorksOffline"),
                  desc: t("WorksOfflineDesc"),
                },
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 13,
                    padding: "12px 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "#eff6ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#2563eb",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <p
                      style={{
                        fontFamily: "'Playfair Display',serif",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#1e293b",
                        marginBottom: 3,
                      }}
                    >
                      {item.title}
                    </p>
                    <p
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: 12,
                        color: "#64748b",
                        lineHeight: 1.7,
                      }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}

              <p
                style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  margin: "20px 0 12px",
                }}
              >
                {t("DownloadNow")}
              </p>
              {[
                {
                  href: GOOGLE_PLAY,
                  bg: "#1a1a1a",
                  label: t("GetItOnGooglePlay"),
                  name: t("GooglePlay"),
                  icon: (
                    <svg
                      style={{ width: 24, height: 24 }}
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                  ),
                },
                {
                  href: APP_STORE,
                  bg: "#1a1a1a",
                  label: t("DownloadOnTheAppStore"),
                  name: t("AppStore"),
                  icon: (
                    <svg
                      style={{ width: 24, height: 24 }}
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                    </svg>
                  ),
                },
              ].map((st) => (
                <a
                  key={st.href}
                  href={st.href}
                  onClick={openStore(st.href)}
                  className="nt-store"
                  style={s.storeBtn}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: st.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {st.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: 10,
                        color: "#94a3b8",
                        marginBottom: 2,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {st.label}
                    </p>
                    <p
                      style={{
                        fontFamily: "'Playfair Display',serif",
                        fontSize: isMobile ? 15 : 17,
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {st.name}
                    </p>
                  </div>
                  <ArrowRight
                    size={14}
                    style={{ color: "#94a3b8", flexShrink: 0 }}
                    className="nt-arrow"
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Screenshots */}
          <div style={{ marginTop: 52 }}>
            <p
              style={{
                fontFamily: "'Playfair Display',serif",
                fontSize: 22,
                fontWeight: 700,
                textAlign: "center",
                color: "#1e293b",
                marginBottom: 22,
              }}
            >
              {t("SeeItInAction")}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)",
                gap: isMobile ? 10 : 16,
              }}
            >
              {[Image1, Image2, Image3, Image4].map((img, i) => (
                <div
                  key={i}
                  className="nt-shot"
                  style={{
                    aspectRatio: "9/16",
                    borderRadius: 16,
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    overflow: "hidden",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  }}
                >
                  <img
                    src={img}
                    alt={`Screenshot ${i + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ BENEFITS ═══════════════ */}
      <section
        id="benefits"
        style={{
          ...s.section,
          padding: `${sectionPy}px ${px}px`,
          background: "#fff",
        }}
      >
        <div style={s.inner}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr",
              gap: isTablet ? 36 : 56,
              alignItems: "start",
            }}
          >
            <div>
              <p style={s.eyebrow}>✦ {t("WhyProfessionalsChoose")} ✦</p>
              <h2 style={{ ...s.h2, textAlign: "left", marginBottom: 10 }}>
                <em style={{ fontStyle: "italic", color: "#2563eb" }}>
                  {t("NeholTorem")}
                </em>
              </h2>
              <p style={{ ...s.secSub, textAlign: "left", margin: "0 0 28px" }}>
                {t("BenefitsDescription")}
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {benefits.map((b, i) => (
                  <div key={i} className="nt-bcard" style={s.bCard}>
                    <div style={s.bIcon}>{b.icon}</div>
                    <div>
                      <p
                        style={{
                          fontFamily: "'Playfair Display',serif",
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#1e293b",
                          marginBottom: 4,
                        }}
                      >
                        {b.title}
                      </p>
                      <p
                        style={{
                          fontFamily: "'DM Sans',sans-serif",
                          fontSize: 13,
                          color: "#64748b",
                          lineHeight: 1.7,
                        }}
                      >
                        {b.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dashboard card */}
            <div
              style={{
                ...s.dashCard,
                padding: isMobile ? "0 16px 20px" : "0 24px 24px",
              }}
            >
              <div
                style={{
                  height: "3px",
                  background: "linear-gradient(90deg, #2563eb, #60a5fa)",
                  marginBottom: 20,
                  marginLeft: isMobile ? -16 : -24,
                  marginRight: isMobile ? -16 : -24,
                }}
              />
              {[
                { label: t("TodaysAppointments"), val: "24", color: "#16a34a" },
                {
                  label: t("ClientSatisfaction"),
                  val: "4.9 ★",
                  color: "#2563eb",
                },
              ].map((row, i) => (
                <React.Fragment key={i}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 0",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: 12,
                        color: "#64748b",
                      }}
                    >
                      {row.label}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Playfair Display',serif",
                        fontSize: isMobile ? 22 : 26,
                        fontWeight: 700,
                        color: row.color,
                      }}
                    >
                      {row.val}
                    </span>
                  </div>
                  <div style={{ height: 1, background: "#f1f5f9" }} />
                </React.Fragment>
              ))}
              <div style={{ padding: "12px 0" }}>
                <p
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: 12,
                    color: "#64748b",
                    marginBottom: 4,
                  }}
                >
                  {t("ThisMonthRevenue")}
                </p>
                <p
                  style={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: isMobile ? 28 : 34,
                    fontWeight: 700,
                    color: "#2563eb",
                    marginBottom: 4,
                  }}
                >
                  $47,250
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <TrendingUp size={12} style={{ color: "#16a34a" }} />
                  <span
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: 12,
                      color: "#16a34a",
                    }}
                  >
                    {t("PercentageFromLastMonth")}
                  </span>
                </div>
              </div>
              <div
                style={{ height: 1, background: "#f1f5f9", marginBottom: 12 }}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                {[
                  { v: "156", l: t("ActiveClients"), c: "#2563eb" },
                  { v: "98%", l: t("ShowRate"), c: "#16a34a" },
                ].map((m) => (
                  <div
                    key={m.v}
                    style={{
                      padding: 14,
                      background: "#f8faff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{
                        fontFamily: "'Playfair Display',serif",
                        fontSize: isMobile ? 22 : 26,
                        fontWeight: 700,
                        color: m.c,
                      }}
                    >
                      {m.v}
                    </p>
                    <p
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: 11,
                        color: "#94a3b8",
                        marginTop: 4,
                      }}
                    >
                      {m.l}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section
        style={{
          ...s.section,
          padding: `${sectionPy}px ${px}px`,
          background: "#2563eb",
        }}
      >
        <div style={s.inner}>
          <div
            style={{
              textAlign: "center",
              padding: isMobile ? "32px 20px" : "52px 40px",
            }}
          >
            <p style={{ ...s.eyebrow, color: "rgba(255,255,255,0.6)" }}>
              ✦ &nbsp; ✦ &nbsp; ✦
            </p>
            <h2
              style={{
                ...s.h2,
                color: "#fff",
                margin: "14px 0 12px",
                fontSize: isMobile
                  ? "clamp(24px,7vw,38px)"
                  : "clamp(28px,4vw,46px)",
              }}
            >
              {t("ReadyToTransform")}
            </h2>
            <p
              style={{
                ...s.secSub,
                color: "rgba(255,255,255,0.7)",
                marginBottom: 28,
              }}
            >
              {t("CTADescription")}
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
                flexWrap: "wrap",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "14px 28px",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#2563eb",
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  justifyContent: "center",
                  border: "none",
                  cursor: "pointer",
                }}
                className="nt-ctabtn"
                onClick={scrollToApp}
              >
                {t("StartFreeTrial")} <ArrowRight size={15} />
              </button>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "13px 28px",
                  borderRadius: 10,
                  background: "transparent",
                  color: "#fff",
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  justifyContent: "center",
                  border: "2px solid rgba(255,255,255,0.4)",
                  cursor: "pointer",
                }}
                className="nt-ctabtn2"
                onClick={scrollToApp}
              >
                <Download size={15} /> {t("DownloadApp")}
              </button>
            </div>
            <p
              style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 12,
                color: "rgba(255,255,255,0.5)",
                marginTop: 16,
              }}
            >
              {t("FreeTrial")}
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════ CONTACT ═══════════════ */}
      <section
        id="contact"
        style={{
          ...s.section,
          padding: `${sectionPy}px ${px}px`,
          background: "#fff",
        }}
      >
        <div style={s.inner}>
          <div style={s.secHead}>
            <p style={s.eyebrow}>✦ {t("GetInTouch") || "Get In Touch"} ✦</p>
            <h2 style={s.h2}>
              {t("ContactDescription") || "We're Here to Help"}
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "1fr 1fr"
                  : "repeat(3,1fr)",
              gap: isMobile ? 12 : 16,
            }}
          >
            {[
              {
                href: "https://wa.me/972527477128",
                iconBg: "#dcfce7",
                iconColor: "#16a34a",
                border: "#bbf7d0",
                hoverBorder: "#16a34a",
                title: t("WhatsApp") || "WhatsApp",
                sub: t("ChatWithUs") || "Chat with us",
                val: "+972 52-747-7128",
                vc: "#16a34a",
                icon: (
                  <svg
                    style={{ width: 26, height: 26 }}
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    color="#16a34a"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                ),
              },
              {
                href: "tel:+972527477128",
                iconBg: "#dbeafe",
                iconColor: "#2563eb",
                border: "#bfdbfe",
                hoverBorder: "#2563eb",
                title: t("Phone") || "Phone",
                sub: t("CallUs") || "Call us directly",
                val: "+972 52-747-7128",
                vc: "#2563eb",
                icon: <Phone size={26} color="#2563eb" />,
              },
              {
                href: "sms:+972527477128",
                iconBg: "#ede9fe",
                iconColor: "#7c3aed",
                border: "#ddd6fe",
                hoverBorder: "#7c3aed",
                title: t("SMS") || "SMS",
                sub: t("TextUs") || "Send us a text",
                val: "+972 52-747-7128",
                vc: "#7c3aed",
                icon: <MessageSquare size={26} color="#7c3aed" />,
              },
            ].map((c) => (
              <a
                key={c.href}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="nt-ccard"
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "row" : "column",
                  alignItems: isMobile ? "center" : "center",
                  textAlign: isMobile ? "left" : "center",
                  padding: isMobile ? "14px 16px" : "28px 20px",
                  gap: isMobile ? 14 : 14,
                  border: `1.5px solid ${c.border}`,
                  borderRadius: 16,
                  background: "#fff",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                  textDecoration: "none",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{
                    width: isMobile ? 44 : 60,
                    height: isMobile ? 44 : 60,
                    borderRadius: 16,
                    background: c.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {c.icon}
                </div>
                <div>
                  <p
                    style={{
                      fontFamily: "'Playfair Display',serif",
                      fontSize: 17,
                      fontWeight: 700,
                      color: "#1e293b",
                      marginBottom: 3,
                    }}
                  >
                    {c.title}
                  </p>
                  <p
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: 12,
                      color: "#94a3b8",
                      marginBottom: 4,
                    }}
                  >
                    {c.sub}
                  </p>
                  <p
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: 13,
                      fontWeight: 600,
                      color: c.vc,
                    }}
                  >
                    {c.val}
                  </p>
                </div>
              </a>
            ))}
          </div>
          <p
            style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 12,
              color: "#94a3b8",
              textAlign: "center",
              marginTop: 24,
            }}
          >
            {t("ContactAvailability") || "Available 24/7 to assist you"}
          </p>
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer style={{ background: "#1e293b" }}>
        <div
          style={{
            maxWidth: 1160,
            margin: "0 auto",
            padding: isMobile ? `32px ${px}px 20px` : `44px ${px}px 28px`,
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : isTablet
                ? "1fr 1fr"
                : "2fr 1fr 1fr",
            gap: isMobile ? 24 : 36,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  color: "#fff",
                }}
              >
                ✂
              </div>
              <span
                style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "0.04em",
                }}
              >
                NeholTorem
              </span>
            </div>
            <p
              style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 13,
                color: "#94a3b8",
                lineHeight: 1.8,
                maxWidth: 260,
              }}
            >
              {t("FooterDescription") ||
                "Simplifying appointment management for professionals worldwide."}
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p
              style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#64748b",
                marginBottom: 6,
              }}
            >
              {t("QuickLinks") || "Quick Links"}
            </p>
            {["#features", "#app", "#benefits", "#contact"].map((h, i) => (
              <a
                key={h}
                href={h}
                className="nt-flink"
                style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 13,
                  color: "#94a3b8",
                }}
              >
                {
                  [t("Features"), t("MobileApp"), t("Benefits"), t("Contact")][
                    i
                  ]
                }
              </a>
            ))}
          </div>
          <div>
            <p
              style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#64748b",
                marginBottom: 12,
              }}
            >
              {t("FollowUs") || "Follow Us"}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              {[
                {
                  href: "https://www.instagram.com/neholtorem/",
                  label: "Instagram",
                  icon: (
                    <svg
                      style={{ width: 17, height: 17 }}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  ),
                },
                {
                  href: "https://www.facebook.com/profile.php?id=61583627140017",
                  label: "Facebook",
                  icon: (
                    <svg
                      style={{ width: 17, height: 17 }}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  ),
                },
              ].map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nt-social"
                  style={{
                    ...s.socialIcon,
                    background: "#2d3748",
                    borderColor: "#4a5568",
                    color: "#94a3b8",
                  }}
                  aria-label={item.label}
                >
                  {item.icon}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            maxWidth: 1160,
            margin: "0 auto",
            padding: isMobile ? `14px ${px}px 24px` : `18px ${px}px 28px`,
            borderTop: "1px solid #2d3748",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <p
            style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 12,
              color: "#64748b",
            }}
          >
            {t("CopyrightMainPage")}
          </p>
          <div style={{ display: "flex", gap: 20 }}>
            {[t("Privacy"), t("Terms")].map((lbl) => (
              <a
                key={lbl}
                href="#"
                className="nt-flink"
                style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                {lbl}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainPage;

/* ══════════════════════════════════════ CSS ══════════════════════════════════════ */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
  html { -webkit-text-size-adjust:100%; }
  body { background:#fff; overflow-x:hidden; }
  a { text-decoration:none; color:inherit; }
  button { cursor:pointer; border:none; background:none; }

  @keyframes slideUp { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:translateY(0); } }
  @keyframes phoneF { 0%,100% { transform:translateY(0) rotate(-1deg); } 50% { transform:translateY(-18px) rotate(1deg); } }

  .nt-up   { animation: slideUp 0.65s ease-out forwards; }
  .nt-phone { animation: phoneF 5s ease-in-out infinite; }

  .nt-primary { background: #2563eb !important; color: #fff !important; transition: all .22s !important; border-radius: 10px !important; }
  .nt-primary:hover { background: #1d4ed8 !important; box-shadow: 0 6px 24px rgba(37,99,235,.35) !important; transform: translateY(-2px) !important; }

  .nt-ghost { background: transparent !important; border: 1.5px solid #e2e8f0 !important; color: #64748b !important; transition: all .22s !important; border-radius: 10px !important; }
  .nt-ghost:hover { border-color: #2563eb !important; color: #2563eb !important; }

  .nt-navlink { transition: color .18s; font-family: 'DM Sans', sans-serif; font-size: 14px; color: #64748b; }
  .nt-navlink:hover { color: #2563eb !important; }

  .nt-topbtn { transition: all .18s; }
  .nt-topbtn:hover { opacity: 0.8; transform: translateY(-1px); }

  .nt-fcard { transition: transform .28s, box-shadow .28s; }
  .nt-fcard:hover { transform: translateY(-5px); }

  .nt-store { transition: border-color .2s, box-shadow .2s; }
  .nt-store:hover { border-color: #2563eb !important; box-shadow: 0 4px 16px rgba(37,99,235,.12) !important; }
  .nt-store:hover .nt-arrow { color: #2563eb !important; }

  .nt-shot { transition: transform .28s, box-shadow .28s; }
  .nt-shot:hover { transform: translateY(-5px) scale(1.02); box-shadow: 0 12px 32px rgba(0,0,0,0.12) !important; }

  .nt-bcard { transition: border-color .2s, box-shadow .2s; }
  .nt-bcard:hover { border-color: #2563eb !important; box-shadow: 0 4px 16px rgba(37,99,235,.1) !important; }

  .nt-ccard { transition: border-color .2s, transform .24s, box-shadow .2s !important; }
  .nt-ccard:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0,0,0,0.1) !important; }

  .nt-flink { transition: color .18s; }
  .nt-flink:hover { color: #93c5fd !important; }

  .nt-social { transition: border-color .18s, color .18s, background .18s; }
  .nt-social:hover { border-color: #2563eb !important; color: #2563eb !important; }

  .nt-lang-btn { transition: border-color .18s, background .18s; }
  .nt-lang-btn:hover { border-color: #2563eb !important; background: #eff6ff !important; }

  .nt-dropitem { transition: background .15s, color .15s; }
  .nt-dropitem:hover { background: #eff6ff !important; color: #2563eb !important; }

  .nt-herobtn { transition: all .2s; }
  .nt-herobtn:hover { border-color: #2563eb !important; box-shadow: 0 6px 20px rgba(37,99,235,.14) !important; transform: translateY(-2px); }

  .nt-herocontact { transition: all .2s; }
  .nt-herocontact:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,.1); }

  .nt-ctabtn { transition: all .2s; }
  .nt-ctabtn:hover { transform: translateY(-2px); box-shadow: 0 6px 24px rgba(255,255,255,.3); }

  .nt-ctabtn2 { transition: all .2s; }
  .nt-ctabtn2:hover { border-color: rgba(255,255,255,0.8) !important; transform: translateY(-2px); }

  @media (max-width:639px) {
    .nt-desk { display:none!important; }
  }
  @media (min-width:640px) {
    .nt-mob { display:none!important; }
  }
`;

/* ══════════════════════════════════════ STYLES ══════════════════════════════════════ */
const s = {
  root: {
    background: "#fff",
    minHeight: "100vh",
    color: "#1e293b",
    position: "relative",
    overflowX: "hidden",
  },
  section: { position: "relative", zIndex: 1 },
  inner: { maxWidth: 1160, margin: "0 auto" },
  secHead: { textAlign: "center", marginBottom: 48 },
  eyebrow: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: "11px",
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginBottom: 12,
  },
  h1: {
    fontFamily: "'Playfair Display',serif",
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: "0.01em",
    color: "#1e293b",
  },
  h2: {
    fontFamily: "'Playfair Display',serif",
    fontSize: "clamp(26px,5vw,48px)",
    fontWeight: 700,
    color: "#1e293b",
    letterSpacing: "0.01em",
    marginBottom: 14,
  },
  heroSub: {
    fontFamily: "'DM Sans',sans-serif",
    letterSpacing: "0.01em",
    color: "#64748b",
    lineHeight: 1.8,
    maxWidth: 520,
    margin: "0 auto",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 18px",
    border: "1.5px solid #dbeafe",
    borderRadius: 40,
    marginBottom: 20,
    background: "#eff6ff",
  },
  badgeText: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 12,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#2563eb",
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 28px",
    borderRadius: 10,
    fontFamily: "'DM Sans',sans-serif",
    fontSize: "14px",
    fontWeight: 700,
  },
  btnGhost: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "13px 28px",
    borderRadius: 10,
    fontFamily: "'DM Sans',sans-serif",
    fontSize: "14px",
    fontWeight: 600,
  },

  /* Top bar */
  topBar: {
    background: "#f8faff",
    borderBottom: "1px solid #e2e8f0",
    position: "relative",
    zIndex: 51,
  },
  topBarInner: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "8px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  topBarLabel: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 12,
    color: "#94a3b8",
    fontWeight: 500,
  },
  topContactBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontFamily: "'DM Sans',sans-serif",
    fontWeight: 600,
    textDecoration: "none",
  },
  topStoreBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 20,
    fontSize: 12,
    fontFamily: "'DM Sans',sans-serif",
    fontWeight: 600,
    textDecoration: "none",
  },

  /* Nav */
  nav: {
    position: "fixed",
    top: 36,
    left: 0,
    right: 0,
    zIndex: 50,
    transition: "background .3s, box-shadow .3s, top .3s",
    borderBottom: "1px solid transparent",
  },
  navScrolled: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(16px)",
    borderBottom: "1px solid #e2e8f0",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
    top: 0,
  },
  navRow: {
    maxWidth: 1200,
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  navLogo: { display: "flex", alignItems: "center", gap: 10 },
  logoMark: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: "#fff",
    fontWeight: 700,
    flexShrink: 0,
  },
  brandName: {
    fontFamily: "'Playfair Display',serif",
    fontWeight: 700,
    letterSpacing: "0.04em",
    color: "#1e293b",
  },
  desktopLinks: { display: "flex", alignItems: "center", gap: 20 },
  navLink: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 14,
    color: "#64748b",
    fontWeight: 500,
  },
  navCta: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: "14px",
    fontWeight: 700,
    padding: "10px 20px",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  burger: {
    background: "transparent",
    border: "none",
    padding: 4,
    flexShrink: 0,
  },
  mobileMenu: {
    padding: "14px 16px 18px",
    background: "#fff",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    boxShadow: "0 12px 32px rgba(0,0,0,0.1)",
  },
  mobileLink: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 14,
    color: "#64748b",
    fontWeight: 500,
    padding: "8px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  socialIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1.5px solid #e2e8f0",
    background: "#f8faff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    flexShrink: 0,
  },

  /* Lang */
  langToggle: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 13,
    cursor: "pointer",
  },
  langToggleName: { color: "#64748b", fontSize: 13 },
  langDrop: {
    position: "absolute",
    top: "calc(100% + 5px)",
    right: 0,
    zIndex: 99,
    background: "#fff",
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    minWidth: 185,
    overflow: "hidden",
    boxShadow: "0 14px 40px rgba(0,0,0,0.12)",
  },
  langDropItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "10px 13px",
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 13,
    color: "#64748b",
    cursor: "pointer",
    width: "100%",
    background: "transparent",
    border: "none",
  },
  langDropActive: { background: "#eff6ff", color: "#2563eb" },
  langBtn: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "10px 13px",
    background: "#fff",
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    width: "100%",
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 13,
    color: "#64748b",
    cursor: "pointer",
  },
  langBtnName: { flex: 1, textAlign: "left" },

  /* Modal */
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    padding: 16,
  },
  modal: {
    background: "#fff",
    border: "1.5px solid #e2e8f0",
    borderRadius: 20,
    maxWidth: 390,
    width: "100%",
    boxShadow: "0 32px 80px rgba(0,0,0,0.15)",
  },
  modalHead: { textAlign: "center", marginBottom: 22 },
  globeRing: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    border: "1.5px solid #dbeafe",
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
  },
  modalTitle: {
    fontFamily: "'Playfair Display',serif",
    fontSize: 25,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 5,
  },
  modalSub: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 13,
    color: "#64748b",
  },
  modalHint: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 11,
    color: "#94a3b8",
    textAlign: "center",
    marginTop: 16,
  },

  /* Features */
  fCard: {
    padding: 24,
    border: "1.5px solid #e2e8f0",
    borderRadius: 16,
    background: "#fff",
    cursor: "default",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  fCardOn: {
    borderColor: "#2563eb",
    background: "#f8faff",
    boxShadow: "0 8px 32px rgba(37,99,235,0.12)",
  },
  fIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: "1.5px solid #e2e8f0",
    background: "#f8faff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    marginBottom: 16,
    transition: "all .3s",
  },
  fIconOn: {
    background: "#2563eb",
    border: "none",
    color: "#fff",
    boxShadow: "0 4px 16px rgba(37,99,235,.35)",
  },
  fTitle: {
    fontFamily: "'Playfair Display',serif",
    fontSize: 20,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 10,
  },
  fDesc: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.8,
    marginBottom: 14,
  },
  fStat: {
    display: "inline-block",
    padding: "4px 14px",
    border: "1.5px solid #dbeafe",
    borderRadius: 20,
    background: "#eff6ff",
  },
  fStatText: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 11,
    fontWeight: 600,
    color: "#2563eb",
  },

  /* Hero download + contact */
  heroStoreBtn: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    textDecoration: "none",
  },
  heroContactBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "9px 18px",
    borderRadius: 24,
    border: "1.5px solid",
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  },

  /* Store */
  storeBtn: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 16px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 14,
    background: "#fff",
    marginBottom: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },

  /* Benefits */
  bCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "14px 16px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 14,
    background: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  bIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0,
  },
  dashCard: {
    background: "#fff",
    border: "1.5px solid #e2e8f0",
    borderRadius: 20,
    boxShadow: "0 20px 56px rgba(37,99,235,0.08)",
  },
  secSub: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 14,
    color: "#64748b",
    lineHeight: 1.8,
    maxWidth: 520,
    margin: "0 auto",
  },
};
